import LoginFailure from '@/components/auth/login/components/LoginFailure';
import type { SignInFormData } from '@/components/auth/login/types';
import settings from '@/constants/constants';
import { FF_ENABLE_ESCRIPT_EXPIRED_INVITE_RECOVERY } from '@/constants/featureFlags';
import { useProcessCustomerOnboarding } from '@/hooks/auth/useProcessCustomerOnboarding';
import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import { Logger } from '@/utils/logger';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  LoadingButton,
  Stack,
  TextField,
  toast,
  Typography,
} from '@montugroup/design-system';
import { BrandThemeProvider } from '@montugroup/themes';
import { FormControlLabel, FormHelperText, styled } from '@mui/material';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { generatePath, Link, matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AuthCardContainer,
  AuthPageContainer,
  CardHalfContent,
  CardHalfDecoration,
  ForgotPasswordContainer,
  FormContainer,
  MobileLogoContainer,
} from '../../components/auth/authPageCustomer';

import {
  UMEDS_ACCESS_PRESCRIPTION_CONSENT_TEXT,
  UMEDS_MARKETING_CONSENT_TEXT,
} from '@/components/order/order-summary/UmedsMarketingConsent';
import { GoogleAnalyticsEscriptEventName } from '@/hooks/analytics/types';
import useGoogleAnalytics from '@/hooks/analytics/useGoogleAnalytics';
import { hashedEmail, hashedGooglePhone, hashedMetaPhone } from '@/hooks/analytics/utils';
import type { TOnboardKeyStatusData, TOnboardKeyStatusError } from '@/hooks/auth/useGetOnboardKeyStatus';
import { useGetOnboardKeyStatus } from '@/hooks/auth/useGetOnboardKeyStatus';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import usePageMetadata from '@/hooks/usePageMetadata';
import {
  ESCRIPT_INVITE_EXPIRED_ROUTE,
  ESCRIPT_ONBOARDING_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  HOME_ROUTE,
} from '@/routes/route-paths';
import { HttpStatus } from '@montugroup/apis-styleguide';
import type { apiV1Contract } from '@montugroup/circuit-api-contracts';
import { UserPasswordSchema } from '@montugroup/circuit-api-contracts';
import { ShowPasswordAdornment, useAuthCheck, useLogin, zodEmailValidate } from '@montugroup/circuit-shared';
import { CodeInputDialog } from '@montugroup/circuit-shared/components/common/CodeInputDialog';
import { PasswordRequirements } from '@montugroup/circuit-shared/components/common/PasswordRequirements';
import { viteDomainSplitBaseUrl } from '@montugroup/circuit-shared/constants/env';
import type { MontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { isMontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { emitUIEvent } from '@montugroup/data-collection';
import { isErrorResponse } from '@ts-rest/core';

const logger = new Logger('LogIn');

const AuthCardBackground = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.primary.main,
  backgroundPosition: 'top left',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-start',
  padding: '2rem',
  borderTopLeftRadius: '25px',
  borderBottomLeftRadius: '25px',
}));

// TODO: confirm the correct metadata details
const pageTitle = 'Login | uMeds Customer Portal';
const pageDescription = 'Log in to your uMeds customer portal to upload your eScripts or order your medicine.';

const isNotFound = (error: TOnboardKeyStatusError): boolean =>
  'status' in error && error.status === HttpStatus.NOT_FOUND;

export function LogIn() {
  /**
   * *************
   * Hooks
   **************
   */
  const { logoutReason } = useAuthCheck({ defaultAuthedUserRoute: HOME_ROUTE });
  const { key: brandName, Icon: Bg, Logo } = usePortalPartnerBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const {
    getOnboardKeyStatusMutate,
    onboardKeyStatus,
    isPending: isOnboardKeyStatusPending,
  } = useGetOnboardKeyStatus();
  const {
    mutate: externalCustomerSignupMutation,
    isPending: isExternalSignupPending,
    contractEndpoint,
  } = useProcessCustomerOnboarding();
  const isPatientOnboardingSignup = matchPath({ path: '/login/patient/:onBoardKey' }, location.pathname);
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics();
  const { login, isPending: loginIsPending, mfaController } = useLogin();
  const isExpiredInviteRecoveryEnabled = Boolean(useFeatureFlag(FF_ENABLE_ESCRIPT_EXPIRED_INVITE_RECOVERY));

  const [isPasswordRevealed, setIsPasswordRevealed] = useState<boolean>(false);
  const [isConfirmPasswordRevealed, setIsConfirmPasswordRevealed] = useState<boolean>(false);

  usePageMetadata({
    title: pageTitle,
    'og:title': pageTitle,
    description: pageDescription,
    'og:description': pageDescription,
  });

  /**
   * *************
   * State
   **************
   */
  const [submissionError, setSubmissionError] = useState<string | MontuError<typeof apiV1Contract.user.login>>('');
  const [internalLoadingState, setInternalLoadingState] = useState(false);

  /**
   * *************
   * Form Setup
   **************
   */
  const { register, handleSubmit, formState, setValue, getValues, control } = useForm<SignInFormData>({
    defaultValues: {
      email: '',
      phone: '',
      password: '',
      rePassword: '',
      agreementCheckbox: true,
    },
  });
  const { errors } = formState;
  const emailValue = useWatch({ name: 'email', control });
  const phoneValue = useWatch({ name: 'phone', control });

  /**
   * *************
   * Derived State
   **************
   */
  const onBoardKey = params?.onBoardKey || '';
  const loading =
    loginIsPending ||
    internalLoadingState ||
    onboardKeyStatus?.isOnboardKeyExpired ||
    isOnboardKeyStatusPending ||
    isExternalSignupPending;

  /**
   * **********
   * Callbacks
   ************
   */

  const handleOnboardKeySuccess = useCallback(
    (onboardKeyResponseData: TOnboardKeyStatusData) => {
      const {
        body: { data },
      } = onboardKeyResponseData;
      const currentEmail = getValues('email');
      const currentPhone = getValues('phone');
      // Only set a default email if phone does not exist
      if (data.email && currentEmail.toLowerCase() !== data.email.toLowerCase() && !data?.phone) {
        setValue('email', data.email);
      }
      // only update the phone number if provided
      if (data?.phone && currentPhone !== data?.phone) {
        const parsedAU = parsePhoneNumberFromString(data.phone, 'AU');
        const formattedPhone = parsedAU?.isValid() ? parsedAU.format('E.164') : data.phone;

        if (currentPhone !== formattedPhone) {
          setValue('phone', formattedPhone);
        }
      }
    },
    [getValues, setValue],
  );

  // Login handler for useRegister & useSubmit
  const handleLogin = useCallback(
    async (email: string, password: string) => {
      let user;

      try {
        user = await login({ email, password });
      } catch (err) {
        logger.info('AuthService.login failed: ', err);
        setInternalLoadingState(false);
        setSubmissionError(err as string);
      }

      if (!user) {
        return;
      }

      logger.info('navigating to /home');
      navigate(HOME_ROUTE, { replace: true });
    },
    [login, navigate],
  );

  const redirectToExpiredInvite = useCallback(() => {
    emitUIEvent(
      {
        component_name: 'onboarding_login',
        event_name: 'onboarding_invite_error',
        id: 'escript_onboarding_invite',
        value: 'invite link expired',
      },
      [],
    );

    if (!isExpiredInviteRecoveryEnabled) {
      // The behaviour before expired invite recovery. Remove with the enable_escript_expired_invite_recovery flag.
      navigate(ESCRIPT_ONBOARDING_ROUTE, {
        replace: true,
        state: {
          error: {
            title: '',
            body: 'Your invite link has expired. Submit your token again to generate a new link.',
          },
        },
      });
      return;
    }

    navigate(generatePath(ESCRIPT_INVITE_EXPIRED_ROUTE, { onBoardKey: encodeURIComponent(onBoardKey) }), {
      replace: true,
    });
  }, [isExpiredInviteRecoveryEnabled, navigate, onBoardKey]);

  const handleOnboardKeyError = useCallback(
    (error: TOnboardKeyStatusError) => {
      // Before expired invite recovery, any failed check was treated as an expired link.
      if (!isExpiredInviteRecoveryEnabled || isNotFound(error)) {
        redirectToExpiredInvite();
      } else {
        toast.warn("We couldn't check your invite link. Please reload the page.");
      }
    },
    [isExpiredInviteRecoveryEnabled, redirectToExpiredInvite],
  );

  /**
   * **********
   * useEffects
   ************
   */
  useEffect(() => {
    if (!onBoardKey) {
      return;
    }

    if (onboardKeyStatus?.isOnboardKeyExpired) {
      redirectToExpiredInvite();
      return;
    }

    const currentEmail = getValues('email');
    const currentPhone = getValues('phone');

    sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.PRESCRIPTION_SUBMIT_VERIFIED, {
      sha_email: currentEmail ? hashedEmail(currentEmail) : '',
      sha_fb_phone_number: currentPhone ? hashedMetaPhone(currentPhone) : '',
      sha_gads_phone_number: currentPhone ? hashedGooglePhone(currentPhone) : '',
    });

    getOnboardKeyStatusMutate(onBoardKey, handleOnboardKeySuccess, handleOnboardKeyError);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onBoardKey,
    handleOnboardKeySuccess,
    handleOnboardKeyError,
    redirectToExpiredInvite,
    getOnboardKeyStatusMutate,
    onboardKeyStatus?.isOnboardKeyExpired,
  ]);

  /**
   * **********
   * Event Handlers
   ************
   */
  // When user signs up
  const onRegister: SubmitHandler<SignInFormData> = async (formData, e) => {
    e?.preventDefault();
    setInternalLoadingState(true);

    externalCustomerSignupMutation(
      {
        body: {
          data: {
            email: formData.email.toLowerCase().trim(),
            onboard_key: onBoardKey,
            password: formData.password,
            phone: formData.phone,
            umedsMarketingOptIn: formData.agreementCheckbox,
          },
        },
      },
      {
        onSuccess: async () => {
          logger.info('External customer registered');

          // successful GA event
          sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.ACCOUNT_REGISTRATION_COMPLETE, {
            sha_email: hashedEmail(formData.email),
            sha_fb_phone_number: hashedMetaPhone(formData.phone),
            sha_gads_phone_number: hashedGooglePhone(formData.phone),
          });

          // todo: emitUIEvent is an interim approach until the data collection package is updated with 'Data Events' schema
          emitUIEvent(
            {
              component_name: 'onboarding_login',
              event_name: 'onboarding_signup_success',
              id: 'escript_onboarding_signup',
              value: 'successfull external customer sign up',
            },
            [],
          );
          handleLogin(formData.email.toLowerCase().trim(), formData.password);
        },
        onError: (error) => {
          setInternalLoadingState(false);
          emitUIEvent(
            {
              component_name: 'onboarding_login',
              event_name: 'onboarding_signup_error',
              id: 'escript_onboarding_signup',
              value: 'unsuccessfull external customer sign up',
            },
            [],
          );
          if (isErrorResponse(error, contractEndpoint) && isMontuError<typeof contractEndpoint>(error)) {
            if (error.body.error.code === 'password.requirements_not_met') {
              toast.error(
                'The password you entered is invalid as it does not meet our security requirements. Please choose a different password.',
              );
            } else if (error.body.error.message) {
              toast.error(error.body.error.message);
            }
          } else {
            toast.error('Error Occurred! Try again later!');
          }

          logger.error('processCustomerOnboarding', error);
        },
      },
    );
  };

  // When user logs in
  const onSubmit: SubmitHandler<SignInFormData> = async (formData, e) => {
    e?.preventDefault();

    setSubmissionError('');
    setInternalLoadingState(true);

    handleLogin(formData.email.toLowerCase().trim(), formData.password);
  };

  useLayoutEffect(() => {
    if (isPatientOnboardingSignup) {
      window.location.replace(`${viteDomainSplitBaseUrl('patient')}/login/patient/${onBoardKey}`);
    }
  }, [isPatientOnboardingSignup, onBoardKey]);

  /**
   * **********
   * Render Logic
   ************
   */
  return (
    <BrandThemeProvider theme={brandName}>
      <Box width="100%" height="100%" position="relative">
        <AuthPageContainer>
          <AuthCardContainer>
            <CardHalfDecoration>
              <AuthCardBackground>
                <Link to={settings.marketing.site.url} target="_blank" rel="noopener noreferrer">
                  <Logo width="118px" sx={{ position: 'absolute', top: '3.25rem', left: '3.25rem', height: 'auto' }} />
                </Link>
                <Bg sx={{ position: 'absolute', bottom: '4.652rem', right: '0' }} />
              </AuthCardBackground>
            </CardHalfDecoration>
            <CardHalfContent>
              <MobileLogoContainer>
                <Link to={settings.marketing.site.url} target="_blank" rel="noopener noreferrer">
                  <Logo width="150px" height="auto" fillColor={(theme) => theme.palette.primary.main} />
                </Link>
              </MobileLogoContainer>
              <FormContainer>
                <form data-private method="post">
                  <Stack spacing={6}>
                    {!submissionError && logoutReason === 'deactivation' && (
                      <Alert severity="success" variant="outlined">
                        <AlertTitle>Account deactivated</AlertTitle>
                        <Typography variant="body2">
                          Your request has been completed. You can no longer log into uMeds or place orders.
                        </Typography>
                      </Alert>
                    )}
                    <LoginFailure error={submissionError} showSupportText />
                    <Typography variant="h5" component="h2">
                      {onBoardKey ? 'Sign up to order your medication' : 'Login'}
                    </Typography>
                    {onBoardKey && (
                      <Typography sx={(theme) => ({ marginBottom: theme.spacing(4) })}>
                        Please enter your password below. Your password must:
                        <PasswordRequirements isFirstPassword />
                      </Typography>
                    )}
                    <TextField
                      {...register('email', {
                        required: 'You must enter an email address',
                        validate: zodEmailValidate,
                      })}
                      type="text"
                      id="useremailone"
                      error={!!errors.email}
                      helperText={errors.email?.message ? errors.email.message : ''}
                      autoComplete="email"
                      disabled={!!onBoardKey && (loading || (!!onboardKeyStatus?.email && !onboardKeyStatus?.phone))}
                      label="Email"
                      InputLabelProps={{ shrink: emailValue ? !!emailValue : undefined }}
                      fullWidth
                    />
                    {onBoardKey ? (
                      <Controller
                        name="phone"
                        control={control}
                        rules={{
                          required: 'You must enter a phone number',
                          validate: (value: string) => {
                            const parsedAU = parsePhoneNumberFromString(value, 'AU');
                            if (!parsedAU || !parsedAU.isValid()) {
                              return 'You must enter a valid phone number';
                            }
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="tel"
                            id="userphone"
                            label="Phone"
                            fullWidth
                            autoComplete="tel"
                            error={!!errors.phone}
                            helperText={errors.phone?.message ? errors.phone.message : ''}
                            disabled={!!onBoardKey && (loading || !!onboardKeyStatus?.phone)}
                            InputLabelProps={{ shrink: phoneValue ? !!phoneValue : undefined }}
                            onBlur={(e) => {
                              const inputValue = e.target.value;
                              const parsedAU = parsePhoneNumberFromString(inputValue, 'AU');
                              if (parsedAU?.isValid()) {
                                field.onChange(parsedAU.format('E.164')); // AU numbers convert to E.164 format, Braze requirement
                              } else {
                                field.onChange(inputValue); // Non-AU numbers stay as is
                              }
                            }}
                          />
                        )}
                      />
                    ) : null}

                    <Stack>
                      <TextField
                        {...register('password', {
                          required: 'You must enter a password',
                          validate: {
                            isValid: (value) => {
                              if (!onBoardKey) {
                                // For login
                                return true;
                              }

                              // For onboarding
                              return UserPasswordSchema.safeParse(value).error?.issues[0].message;
                            },
                          },
                        })}
                        type={isPasswordRevealed ? 'text' : 'password'}
                        id="userpasswordone"
                        error={!!errors.password}
                        helperText={errors.password ? errors.password.message : ''}
                        label={onBoardKey ? 'Enter new password' : 'Password'}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <ShowPasswordAdornment isRevealed={isPasswordRevealed} toggle={setIsPasswordRevealed} />
                          ),
                        }}
                      />

                      {!onBoardKey && (
                        <ForgotPasswordContainer>
                          <Link to={FORGOT_PASSWORD_ROUTE}>
                            <FormHelperText sx={{ textDecoration: 'underline' }}>Forgot Password?</FormHelperText>
                          </Link>
                        </ForgotPasswordContainer>
                      )}
                    </Stack>

                    {onBoardKey ? (
                      <>
                        <TextField
                          {...register('rePassword', {
                            required: 'Please confirm your password',
                            validate: (val, formData) => val === formData.password || 'Your passwords must match',
                          })}
                          type={isConfirmPasswordRevealed ? 'text' : 'password'}
                          id="re-password"
                          autoComplete="off"
                          label="Confirm password"
                          error={!!errors.rePassword}
                          helperText={errors.rePassword ? errors.rePassword.message : ''}
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <ShowPasswordAdornment
                                isRevealed={isConfirmPasswordRevealed}
                                toggle={setIsConfirmPasswordRevealed}
                              />
                            ),
                          }}
                        />

                        <Controller
                          name="agreementCheckbox"
                          control={control}
                          render={({ field: { onChange, value, ref } }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  data-tracking-id="escript_onboarding_marketing_optin"
                                  checked={value}
                                  onChange={onChange}
                                  inputRef={ref}
                                  color="primary"
                                />
                              }
                              label={<Typography variant="body2">{UMEDS_MARKETING_CONSENT_TEXT}</Typography>}
                            />
                          )}
                        />
                        <Typography variant="body2">{UMEDS_ACCESS_PRESCRIPTION_CONSENT_TEXT}</Typography>
                      </>
                    ) : null}

                    {onBoardKey ? (
                      <Button
                        type="submit"
                        onClick={handleSubmit(onRegister)}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        data-testid="onboarding-login-button"
                        data-tracking-id="escript_onboarding_login_set_password"
                      >
                        Set password
                      </Button>
                    ) : (
                      <LoadingButton
                        type="submit"
                        id="btnSubmit"
                        variant="contained"
                        color="primary"
                        loading={loading}
                        data-testid="login-button"
                        onClick={handleSubmit(onSubmit)}
                      >
                        Login
                      </LoadingButton>
                    )}
                  </Stack>
                </form>
              </FormContainer>
            </CardHalfContent>
          </AuthCardContainer>
        </AuthPageContainer>
      </Box>
      <CodeInputDialog
        controller={mfaController}
        helpLink="https://support.umeds.com.au/hc/en-au/articles/14448865489423-Multi-factor-Authentication"
      />
    </BrandThemeProvider>
  );
}

export default LogIn;
